import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type PayerScope,
  type PaymentArrear,
  type PaymentCharge,
  type PaymentMandate,
  type PaymentMethodKind,
  type PaymentProvider,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date, money, periodShort } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';

/**
 * Sposób płatności za Firmeto (E11-S6) — u firmy i u biura, bo zasady są te same.
 *
 * Klient wybiera formę płatności, a operator jest podpowiedziany: domyślny to ten, który przy jego
 * kwocie kosztuje nas najmniej. Klient może go zmienić — to jego karta i jego bank. Formularz
 * karty stoi u operatora, więc po wyborze przeglądarka tam przechodzi i wraca tu; zgoda staje się
 * aktywna, gdy operator ją potwierdzi, zwykle po kilku sekundach.
 *
 * Zaległość jest na górze, z datą przejścia w tryb tylko do odczytu, bo to jedyna rzecz na tym
 * ekranie, przy której liczy się czas.
 */
export function PaymentMethods({
  scope,
  canManage,
  t,
  locale,
}: {
  scope: PayerScope;
  /** Zgodę wydaje i odwołuje właściciel albo administrator; pozostali widzą stan i historię. */
  canManage: boolean;
  t: Dictionary;
  locale: Locale;
}) {
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [mandates, setMandates] = useState<PaymentMandate[]>([]);
  const [charges, setCharges] = useState<PaymentCharge[] | null>(null);
  const [arrears, setArrears] = useState<PaymentArrear[]>([]);
  const [method, setMethod] = useState<PaymentMethodKind>('card');
  const [provider, setProvider] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  // Powrót od operatora: zgoda czeka na jego potwierdzenie, które przychodzi osobno
  const [returned] = useState(() => new URLSearchParams(window.location.search).has('mandate'));

  const load = useCallback(async () => {
    setError(null);
    try {
      const [loadedCharges, loadedArrears] = await Promise.all([api.paymentCharges(scope), api.paymentArrears(scope)]);
      setCharges(loadedCharges);
      setArrears(loadedArrears);
      if (canManage) {
        const [loadedProviders, loadedMandates] = await Promise.all([api.paymentProviders(scope), api.paymentMandates(scope)]);
        setProviders(loadedProviders);
        setMandates(loadedMandates);
      }
    } catch (failure) {
      setError(failureOf(failure));
    }
    // Zależy od pól zakresu, nie od obiektu — strona tworzy go przy każdym renderze
  }, [scope.kind, scope.id, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!returned) return;
    // Adres bez parametrów operatora — odświeżenie strony nie ma udawać drugiego powrotu
    const url = new URL(window.location.href);
    ['mandate', 'provider', 'cancelled', 'session_id'].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', url.toString());
  }, [returned]);

  const forMethod = providers.filter((item) => item.methods.includes(method));
  const fallback = forMethod.find((item) => item.defaultFor.includes(method))?.provider ?? forMethod[0]?.provider ?? '';
  const chosen = forMethod.some((item) => item.provider === provider) ? provider : fallback;
  const active = mandates.find((mandate) => mandate.status === 'active');
  const methods = (['card', 'blik'] as const).filter((kind) => providers.some((item) => item.methods.includes(kind)));

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const started = await api.startMandate(scope, chosen, method, window.location.href);
      if (started.providerToken) {
        // Operator potwierdził zgodę od razu (środowisko lokalne) — nie ma dokąd przechodzić
        const pending = (await api.paymentMandates(scope)).find((mandate) => mandate.status === 'pending' && mandate.provider === chosen);
        if (pending) await api.confirmMandate(scope, pending.id, started.providerToken, started.label);
        await load();
      } else {
        window.location.assign(started.redirectUrl);
      }
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(mandateId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.revokeMandate(scope, mandateId);
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <ErrorAlert error={error} t={t} />

      {arrears.map((arrear) => {
        const readOnly = arrear.readOnlyFrom <= today;
        return (
          <div key={arrear.period} className={readOnly ? 'alert a-bad' : 'alert a-warn'}>
            <Icon.Warn />
            <div>
              <b>{readOnly ? t.payments.readOnlyTitle : t.payments.arrearTitle(periodShort(arrear.period))}</b>
              <p>
                {t.payments.arrearText(money(arrear.amountGross, locale), t.payments.reasons[arrear.reason] ?? arrear.reason)}{' '}
                {readOnly ? t.payments.readOnlyText : t.payments.readOnlyFrom(date(arrear.readOnlyFrom, locale))}
              </p>
            </div>
          </div>
        );
      })}

      {returned && (
        <div className="alert a-ok">
          <Icon.Info />
          <div>
            <b>{t.payments.waitingTitle}</b>
            <p>{t.payments.waitingText}</p>
          </div>
        </div>
      )}

      <div className="grid two">
        <div className="panel">
          <header>
            <h4>{t.payments.title}</h4>
          </header>
          {!canManage ? (
            <p className="muted sm">{t.payments.onlyOwner}</p>
          ) : (
            <>
              {active ? (
                <dl className="brk">
                  <dt>{t.payments.current}</dt>
                  <dd>
                    <b>{active.label ?? t.payments.methods[active.method] ?? active.method}</b>
                    <div className="muted sm">
                      {active.provider}
                      {active.expiresOn ? ` · ${t.payments.expires(active.expiresOn.slice(0, 7))}` : ''}
                    </div>
                  </dd>
                </dl>
              ) : (
                <p className="muted sm">{t.payments.none}</p>
              )}

              {methods.length > 0 && (
                <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <label className="fld">
                    <span>{t.payments.method}</span>
                    <select className="inp" value={method} onChange={(event) => setMethod(event.target.value as PaymentMethodKind)}>
                      {methods.map((kind) => (
                        <option key={kind} value={kind}>
                          {t.payments.methods[kind]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="fld">
                    <span>{t.payments.provider}</span>
                    <select className="inp" value={chosen} onChange={(event) => setProvider(event.target.value)}>
                      {forMethod.map((item) => (
                        <option key={item.provider} value={item.provider}>
                          {t.payments.providerName(item.provider)}
                          {item.defaultFor.includes(method) ? ` — ${t.payments.recommended}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="btn" disabled={busy || !chosen} onClick={() => void start()}>
                    {active ? t.payments.change : t.payments.add}
                  </button>
                </div>
              )}
              <p className="muted sm">{t.payments.formAtProvider}</p>

              {active && (
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void revoke(active.id)}>
                  {t.payments.revoke}
                </button>
              )}
            </>
          )}
        </div>

        <div className="panel">
          <header>
            <h4>{t.payments.charges}</h4>
          </header>
          {charges?.length === 0 && <p className="muted sm">{t.payments.noCharges}</p>}
          {charges && charges.length > 0 && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.payments.period}</th>
                    <th className="n">{t.payments.amount}</th>
                    <th>{t.payments.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((charge) => (
                    <tr key={charge.id}>
                      <td className="doc">{charge.period ? periodShort(charge.period) : '—'}</td>
                      <td className="n">{amount(charge.amountGross, locale)}</td>
                      <td>
                        <span className={charge.status === 'succeeded' ? 'chip st-ok' : charge.status === 'failed' ? 'chip st-bad' : 'chip st-draft'}>
                          <i />
                          {t.payments.statuses[charge.status]}
                        </span>
                        {charge.failureMessage && <div className="muted sm">{charge.failureMessage}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
